import crypto from 'crypto';
import { z } from 'zod';

export const getGmoCreditToken = async (
  credit: CreditParams
): Promise<CreateTokenResponse> => {
  const response = await generatePublicCreditToken({
    cardNumber: credit.cardno,
    cardholderName: credit.holdername,
    expiryMonth: credit.expire.slice(2),
    expiryYear: credit.expire.slice(0, 2),
    securityCode: credit.securitycode,
  });

  if (!response.success) {
    return {
      success: false,
      errorList: response.errorList,
    };
  }
  const token = (() => {
    if (typeof response.tokenObj.token === 'string') {
      return response.tokenObj.token;
    }
    return response.tokenObj.token[0];
  })();
  return {
    success: true,
    tokenObj: {
      token,
      toBeExpiredAt: response.tokenObj.toBeExpiredAt,
      maskedCardNo: response.tokenObj.maskedCardNo,
      isSecurityCodeSet: response.tokenObj.isSecurityCodeSet,
    },
  };
};

type CreditParams = {
  cardno: string;
  expire: string;
  holdername: string;
  securitycode: string;
};

const createTokenSuccessResponseSchema = z.object({
  success: z.literal(true),
  tokenObj: z.object({
    token: z.string(),
    toBeExpiredAt: z.string(),
    maskedCardNo: z.string(),
    isSecurityCodeSet: z.boolean(),
  }),
});
export type CreateTokenSuccessResponse = z.infer<
  typeof createTokenSuccessResponseSchema
>;

const createTokenErrorResponseSchema = z.object({
  success: z.literal(false),
  errorList: z.array(
    z.object({
      title: z.string(),
      field: z.string(),
      detail: z.string(),
      legacyCode: z.string(),
    })
  ),
});
export type CreateTokenErrorResponse = z.infer<
  typeof createTokenErrorResponseSchema
>;

export const createTokenResponseSchema = z.discriminatedUnion('success', [
  createTokenSuccessResponseSchema,
  createTokenErrorResponseSchema,
]);
type CreateTokenResponse = z.infer<typeof createTokenResponseSchema>;

const successResponseSchema = z.object({
  result: z.literal('success'),
  maskedCardNumber: z.string(),
  tokenExpiryDate: z.string(),
  isSecurityCodeSet: z.boolean(),
  tokenList: z.array(z.string()).min(1),
});
const errorResponseSchema = z.object({
  result: z.union([
    z.literal('validation_error'),
    z.literal('decryption_error'),
  ]),
  errorList: z.array(
    z.object({
      title: z.string(),
      field: z.string(),
      detail: z.string(),
      legacyCode: z.string(),
    })
  ),
});

export const generatePublicTokenCreditInfoSchema = z.object({
  cardNumber: z.string(),
  cardholderName: z.string(),
  expiryMonth: z.string(),
  expiryYear: z.string(),
  securityCode: z.string(),
});
export type GeneratePublicTokenCreditInfo = z.infer<
  typeof generatePublicTokenCreditInfoSchema
>;
export const generatePublicCreditToken = async (
  card: GeneratePublicTokenCreditInfo
): Promise<CreateTokenResponse> => {
  const encodedEncryptedCredit = encryptBody(
    process.env.NEXT_PUBLIC_GMO_V2_PUBLIC_KEY ?? '',
    card
  );

  const params = {
    encryptionParameters: {
      type: 'UNIQUE_PK',
      apiKey: process.env.NEXT_PUBLIC_GMO_V2_API_KEY,
    },
    encryptedData: encodedEncryptedCredit,
  };

  const res = await fetch(process.env.NEXT_PUBLIC_GMO_V2_TOKEN_URL ?? '', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  const data = await res.json();

  const result = successResponseSchema.safeParse(data);

  if (result.success) {
    return {
      success: true,
      tokenObj: {
        token: result.data.tokenList[0],
        toBeExpiredAt: result.data.tokenExpiryDate,
        maskedCardNo: result.data.maskedCardNumber,
        isSecurityCodeSet: result.data.isSecurityCodeSet,
      },
    };
  }

  const errorReponseResult = errorResponseSchema.safeParse(data);
  if (errorReponseResult.success) {
    return {
      success: false,
      errorList: errorReponseResult.data.errorList,
    };
  }

  throw new Error('Invalid response');
};

export type EncryptGmoCardBody = {
  cardNumber: string;
  cardholderName: string;
  expiryMonth: string;
  expiryYear: string;
  securityCode: string;
};
const encryptBody = (publicKey: string, card: EncryptGmoCardBody) => {
  const body = {
    card,
  };
  const publicToken = `-----BEGIN PUBLIC KEY-----
${publicKey}
-----END PUBLIC KEY-----
`;

  const encryptedCreditInfo = crypto.publicEncrypt(
    {
      key: publicToken,
      padding: crypto.constants.RSA_PKCS1_PADDING,
    },
    Buffer.from(JSON.stringify(body))
  );
  return encryptedCreditInfo.toString('base64');
};
